/**
 * v3.12.3 — useFocusPulse
 *
 * Coverage-Map deep-link helper. When a page loads with `?focus={id}` and
 * an array of rows hydrates, this hook scrolls the matching row into
 * view and applies a CSS pulse class for ~2.5s.
 *
 * Why a hook (instead of inlining the useEffect):
 *   1. Single source of truth for the focus selector + pulse duration —
 *      keeps GapAnalysisLive / ComplianceMatrix / CapabilityMatrix /
 *      RequirementsTable consistent.
 *   2. Robustness: defers the DOM lookup behind requestAnimationFrame
 *      and retries every 200ms for up to 3s. Some tools (capability
 *      matrix tab, hidden RTM rows under filters) don't paint the row
 *      synchronously when `rows` first updates, which made the original
 *      one-shot useEffect miss.
 *   3. Clean teardown: clears every retry timer + removes the pulse
 *      class on unmount so pages remounting from a deep-link don't leak
 *      stale animations.
 *
 * Usage:
 *   useFocusPulse({
 *     rows,                              // dependency: re-run when rows hydrate / change
 *     getSelector: (focusId) => `[data-testid="gap-row-${focusId}"]`,
 *     // optional — pulse duration override (default 2500ms)
 *     pulseMs: 2500,
 *   });
 */
import { useEffect } from 'react';

const PULSE_CLASS = 'cov-focus-pulse';
const RETRY_INTERVAL_MS = 200;
const RETRY_MAX_MS = 3000;

export default function useFocusPulse({ rows, getSelector, pulseMs = 2500 }) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const focusedId = params.get('focus');
    if (!focusedId) return;
    if (!rows || rows.length === 0) return;

    let stopped = false;
    let pulseTimer;
    let retryTimer;
    let elapsed = 0;
    let appliedEl = null;

    const tryApply = () => {
      if (stopped) return;
      const selector = getSelector(focusedId);
      if (!selector) return;
      const el = document.querySelector(selector);
      if (el) {
        appliedEl = el;
        // Wait one frame so layout is settled before scrollIntoView.
        requestAnimationFrame(() => {
          if (stopped || !appliedEl) return;
          appliedEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          appliedEl.classList.add(PULSE_CLASS);
          pulseTimer = setTimeout(() => {
            appliedEl?.classList.remove(PULSE_CLASS);
          }, pulseMs);
        });
        return;
      }
      elapsed += RETRY_INTERVAL_MS;
      if (elapsed >= RETRY_MAX_MS) return;
      retryTimer = setTimeout(tryApply, RETRY_INTERVAL_MS);
    };

    // Defer the first attempt until after the next paint so React has
    // committed the row to the DOM.
    retryTimer = setTimeout(tryApply, 50);

    return () => {
      stopped = true;
      if (pulseTimer) clearTimeout(pulseTimer);
      if (retryTimer) clearTimeout(retryTimer);
      if (appliedEl) appliedEl.classList.remove(PULSE_CLASS);
    };
  }, [rows, getSelector, pulseMs]);
}
